import { parseMissionImageRef } from "utils/missionDocuments";
import { normalizeMissionRecord } from "./missionLocationUtils";

/** Align with missionInput comments: sourcing lifecycle */
export const PILOT_STATUS_NEEDED = "Pilot Needed";
export const PILOT_STATUS_SOURCED = "Pilot Sourced";
export const CARGO_STATUS_NEEDED = "Cargo Needed";
export const CARGO_STATUS_SOURCED = "Cargo Sourced";
/** Values sent from MissionForm checkbox (schema: Needed | Sourced). */
export const CARGO_FORM_NEEDED = "Needed";
export const CARGO_FORM_SOURCED = "Sourced";

export function normalizeCargoStatusForForm(status) {
  const s = String(status || "").trim().toLowerCase();
  if (!s) return "";
  if (s.includes("sourced")) return CARGO_FORM_SOURCED;
  if (s.includes("needed")) return CARGO_FORM_NEEDED;
  return "";
}

export function isCargoStatusSourced(status) {
  return normalizeCargoStatusForForm(status) === CARGO_FORM_SOURCED;
}

function parseNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Required total lbs for one line: count × weight (count defaults to 1 when missing). */
export function cargoLineRequiredLbs(item) {
  if (!item) return 0;
  const w = parseNum(item.weightLbs);
  const cnt = parseNum(item.count);
  const units = cnt > 0 ? cnt : 1;
  return units * w;
}

export function isCargoFullySourced(cargoItems) {
  if (!Array.isArray(cargoItems) || cargoItems.length === 0) return true;
  return cargoItems.every((item) => {
    const req = cargoLineRequiredLbs(item);
    if (req <= 0) return true;
    const src = parseNum(item.sourcedWeightLbs);
    return src >= req;
  });
}

export function deriveCargoStatus(cargoItems) {
  return isCargoFullySourced(cargoItems)
    ? CARGO_STATUS_SOURCED
    : CARGO_STATUS_NEEDED;
}

function legEndpoints(leg) {
  if (!leg) return { start: "", end: "" };
  const start = leg.startAirport ?? leg.departureAirportId ?? "";
  const end = leg.endAirport ?? leg.arrivalAirportId ?? "";
  return { start: String(start).trim(), end: String(end).trim() };
}

function legPilotId(leg) {
  if (!leg) return "";
  const pid = leg.pilotId ?? leg.pilot?.profileId ?? "";
  if (String(pid).trim()) return String(pid).trim();
  const name = leg.pilot?.name ?? leg.pilotName ?? "";
  return String(name).trim();
}

/** Legs from MissionForm or mission API: pilot sourced when every routed leg has a pilot. */
export function isPilotFullySourced(legs) {
  if (!Array.isArray(legs) || legs.length === 0) return false;
  const meaningful = legs.filter((leg) => {
    const { start, end } = legEndpoints(leg);
    return start !== "" || end !== "";
  });
  if (meaningful.length === 0) return false;
  return meaningful.every((leg) => legPilotId(leg) !== "");
}

export function derivePilotStatus(legs) {
  return isPilotFullySourced(legs)
    ? PILOT_STATUS_SOURCED
    : PILOT_STATUS_NEEDED;
}

/** Prefer leg assignments over stale API pilotStatus. */
export function resolvePilotStatus(mission) {
  if (!mission || typeof mission !== "object") return "";
  const legs = Array.isArray(mission.legs) ? mission.legs : [];
  if (isPilotFullySourced(legs)) return PILOT_STATUS_SOURCED;
  const fromApi =
    mission.pilotStatus != null ? String(mission.pilotStatus).trim() : "";
  if (fromApi) return fromApi;
  return legs.length > 0 ? derivePilotStatus(legs) : "";
}

export function resolveCargoStatus(mission) {
  if (!mission || typeof mission !== "object") return "";
  const fromApi =
    mission.cargoStatus != null ? String(mission.cargoStatus).trim() : "";
  if (fromApi) return fromApi;
  const cargoItems = Array.isArray(mission.cargoItems) ? mission.cargoItems : [];
  return cargoItems.length > 0 ? deriveCargoStatus(cargoItems) : "";
}

export function applyMissionDerivedStatus(mission) {
  if (!mission || typeof mission !== "object") return mission;
  return {
    ...mission,
    pilotStatus: resolvePilotStatus(mission),
    cargoStatus: resolveCargoStatus(mission),
  };
}

/**
 * Map a mission record (e.g. getMissionById) into saveMission / MissionForm-style fields.
 */
export function missionToSavePayload(mission, overrides = {}) {
  if (!mission) return { ...overrides };
  const normalized = normalizeMissionRecord(mission);
  const legs = Array.isArray(normalized.legs) ? normalized.legs : [];
  const mappedLegs = legs.map((l) => ({
    legId: l.legId ?? l.LegId ?? "",
    departureAirportId: l.startAirport ?? l.departureAirportId ?? "",
    arrivalAirportId: l.endAirport ?? l.arrivalAirportId ?? "",
    pilotId: l.pilot?.profileId ?? l.pilotId ?? "",
  }));

  return {
    title: normalized.title ?? "",
    description: normalized.description ?? "",
    status: normalized.status ?? "",
    departureCity: normalized.departureCity ?? "",
    departureState: normalized.departureState ?? "",
    arrivalCity: normalized.arrivalCity ?? "",
    arrivalState: normalized.arrivalState ?? "",
    dateTime: normalized.dateTime ?? "",
    missionCategory: normalized.missionCategory ?? "",
    type: normalized.type ?? "",
    pilotStatus: resolvePilotStatus(normalized) || derivePilotStatus(mappedLegs),
    cargoStatus:
      overrides.cargoStatus ??
      (normalizeCargoStatusForForm(normalized.cargoStatus) ||
        deriveCargoStatus(normalized.cargoItems)),
    livesImpacted: normalized.livesImpacted ?? "",
    missionPOCName: normalized.missionPOCName ?? "",
    missionPOCOrgId: normalized.missionPOCOrgId ?? "",
    missionPOCPhone: normalized.missionPOCPhone ?? "",
    missionPOCProfileId: normalized.missionPOCProfileId ?? "",
    postedBy: normalized.postedBy ?? "",
    minHours: normalized.minHours ?? "",
    minRating: normalized.minRating ?? "",
    linkedRequestId: normalized.linkedRequestId ?? "",
    disasterCode: normalized.disasterCode ?? "",
    cargoItems: normalized.cargoItems,
    passengerItem: normalized.passengerItem || normalized.PassengerItem,
    PassengerItem: normalized.PassengerItem || normalized.passengerItem,
    legs: mappedLegs,
    pilotRequirements:
      normalized.pilotRequirements &&
      typeof normalized.pilotRequirements === "object"
        ? normalized.pilotRequirements
        : undefined,
    image: parseMissionImageRef(normalized.image),
    ...overrides,
  };
}
